/** Default decimals for portfolio/building rollups (not persisted report scalars). */
export const BUILDING_SF_DECIMALS = 0;

/**
 * Decimals for measurement-report areas — matches legacy `decimal(18, 2)` on
 * `CalculatedRentable`, `Usable`, floor/tenant rollups, etc.
 */
export const REPORT_SF_DECIMALS = 2;

/** Format square footage with commas and optional decimal places */
export function formatSqft(
  value: number | null | undefined,
  decimals = BUILDING_SF_DECIMALS,
  /** DEF-378 — unit suffix; pass `m²` for Square Meters reports. */
  unit = 'SF',
): string {
  if (value == null) return '—';
  return `${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} ${unit}`;
}

/** Format persisted measurement-report square footage (always 2 dp). */
export function formatReportSqft(
  value: number | null | undefined,
  /** DEF-378 — unit suffix; pass `m²` for Square Meters reports. */
  unit = 'SF',
): string {
  return formatSqft(value, REPORT_SF_DECIMALS, unit);
}

/** Format currency (USD) */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
}

/** Format percentage */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '—';
  return `${Number(value).toFixed(decimals)}%`;
}

/** Legacy PST timezone — used in tests to assert backwards-compatible list display. */
export const ORDER_LIST_LEGACY_PST_TIMEZONE = 'America/Los_Angeles';

/**
 * Returns the browser's IANA timezone (`Intl.DateTimeFormat().resolvedOptions().timeZone`).
 */
export function getUserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Short timezone label (e.g. PST, PDT) for a given instant and zone. */
function shortTimeZoneLabel(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(date);
  return parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
}

type DateFormatOptions = {
  /** Override timezone; defaults to the user's local zone. */
  timeZone?: string;
};

/**
 * Splits an order timestamp into localized date + time strings for list/detail grids.
 * Returns null when the input is missing or unparsable.
 */
export function formatOrderDateTime(
  dateString: string | null | undefined,
  options?: DateFormatOptions,
): { date: string; time: string } | null {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  if (!Number.isFinite(parsed.getTime())) return null;

  const timeZone = options?.timeZone ?? getUserTimeZone();
  const date = parsed.toLocaleDateString('en-US', {
    timeZone,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const clock = parsed.toLocaleTimeString('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const tzLabel = shortTimeZoneLabel(parsed, timeZone);
  const time = tzLabel ? `${clock} ${tzLabel}` : clock;

  return { date, time };
}

/**
 * Date + Pacific time for order Message Log and Files (FR-413).
 * Legacy MyAccess `formatDate` appended `toLocaleTimeString()`; Jon asked for PST.
 */
export function formatOrderStampPst(dateString: string | null | undefined): string {
  const stamp = formatOrderDateTime(dateString, { timeZone: ORDER_LIST_LEGACY_PST_TIMEZONE });
  if (!stamp) return '—';
  return `${stamp.date} ${stamp.time}`;
}

/** Format a date string for display in the user's local timezone. */
export function formatDate(dateString: string | null | undefined, options?: DateFormatOptions): string {
  if (!dateString) return '—';
  const parsed = new Date(dateString);
  if (!Number.isFinite(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', {
    timeZone: options?.timeZone ?? getUserTimeZone(),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calendar-day DATE columns (`invoice_date`) arrive as `YYYY-MM-DD` or midnight UTC.
 * `toLocaleDateString` in US zones then shows the previous day. Keep the stored day.
 */
const CALENDAR_DATE_RE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.\d+)?(?:Z)?)?$/;

/** Format a DATE column without shifting it across a timezone. */
export function formatCalendarDate(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const trimmed = dateString.trim();
  const match = CALENDAR_DATE_RE.exec(trimmed);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    if (!Number.isFinite(utcNoon.getTime())) return '—';
    return utcNoon.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  return formatDate(trimmed);
}

/**
 * Compact relative-time formatter — "just now", "5m ago", "3h ago",
 * "2d ago", or falls back to the absolute date once past a week.
 * Used by the notifications bell + activity rows where the absolute
 * date is too noisy at a glance.
 */
export function formatRelative(dateString: string | null | undefined, now: Date = new Date()): string {
  if (!dateString) return '—';
  const then = new Date(dateString).getTime();
  if (!Number.isFinite(then)) return '—';
  const deltaMs = now.getTime() - then;
  const sec = Math.round(deltaMs / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return formatDate(dateString);
}

/** Format a number with commas */
export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  return Number(value).toLocaleString('en-US');
}

/** Format a number in compact notation: 1.2M, 42.7K, 850 */
export function formatCompact(value: number | null | undefined): string {
  if (value == null) return '—';
  const n = Number(value);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

/** Format sqft in compact notation: 187.5K SF */
export function formatSqftCompact(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${formatCompact(value)} SF`;
}

/** Format currency in compact notation: $4.2M, $42.7K */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value == null) return '—';
  return `$${formatCompact(value)}`;
}
